import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";
import { useLang } from "@/i18n/LanguageContext";
import { Compass, Menu } from "lucide-react";

export const LanguageToggle = () => {
  const { lang, setLang, t } = useLang();
  return (
    <div
      data-testid="nav-language-toggle"
      className="flex items-center rounded-xl border border-border bg-card p-0.5"
      role="group"
      aria-label={t.nav.ariaLangSelector}
    >
      <button
        data-testid="lang-fr-button"
        onClick={() => setLang("fr")}
        aria-pressed={lang === "fr"}
        className={`px-2.5 py-1 text-xs font-semibold rounded-[10px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          lang === "fr"
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:bg-secondary"
        }`}
      >
        FR
      </button>
      <button
        data-testid="lang-en-button"
        onClick={() => setLang("en")}
        aria-pressed={lang === "en"}
        className={`px-2.5 py-1 text-xs font-semibold rounded-[10px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          lang === "en"
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:bg-secondary"
        }`}
      >
        EN
      </button>
    </div>
  );
};

export const Navbar = ({ showAnchors = false }) => {
  const { t } = useLang();
  const [mobileOpen, setMobileOpen] = useState(false);

  const anchorLinks = showAnchors ? (
    <>
      <a
        href="#benefits"
        data-testid="nav-benefits-link"
        className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg px-1 py-0.5"
      >
        {t.nav.benefits}
      </a>
      <a
        href="#visas"
        data-testid="nav-visas-link"
        className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg px-1 py-0.5"
      >
        {t.nav.visas}
      </a>
      <a
        href="#faq"
        data-testid="nav-faq-link"
        className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg px-1 py-0.5"
      >
        {t.nav.faq}
      </a>
    </>
  ) : null;

  return (
    <nav className="sticky top-0 z-50 bg-background border-b border-border">
      <div className="container-page flex items-center justify-between h-16">
        <Link
          to="/"
          data-testid="nav-brand-link"
          className="flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Compass className="h-5 w-5" />
          </span>
          <span className="font-heading font-bold text-lg tracking-[-0.02em]">
            {t.nav.brand}
          </span>
        </Link>

        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-6">
          {anchorLinks}
        </div>

        <div className="flex items-center gap-3">
          <LanguageToggle />
          <Button
            asChild
            data-testid="nav-cta-button"
            className="rounded-xl h-10 px-4 active:scale-[0.98] transition-transform duration-150 hidden sm:inline-flex"
          >
            <Link to="/quiz">{t.nav.cta}</Link>
          </Button>

          {/* Mobile hamburger menu */}
          {showAnchors && (
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="nav-mobile-menu-button"
                  className="md:hidden flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-card hover:bg-secondary transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={t.nav.menu}
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-background">
                <SheetTitle className="font-heading font-bold text-lg tracking-[-0.02em] mb-6">
                  {t.nav.brand}
                </SheetTitle>
                <div className="flex flex-col gap-4">
                  <a
                    href="#benefits"
                    onClick={() => setMobileOpen(false)}
                    className="text-base font-medium text-foreground hover:text-primary transition-colors duration-150 py-2"
                  >
                    {t.nav.benefits}
                  </a>
                  <a
                    href="#visas"
                    onClick={() => setMobileOpen(false)}
                    className="text-base font-medium text-foreground hover:text-primary transition-colors duration-150 py-2"
                  >
                    {t.nav.visas}
                  </a>
                  <a
                    href="#faq"
                    onClick={() => setMobileOpen(false)}
                    className="text-base font-medium text-foreground hover:text-primary transition-colors duration-150 py-2"
                  >
                    {t.nav.faq}
                  </a>
                  <div className="border-t border-border pt-4 mt-2">
                    <Button
                      asChild
                      className="w-full rounded-xl h-11 text-base active:scale-[0.98] transition-transform duration-150"
                      onClick={() => setMobileOpen(false)}
                    >
                      <Link to="/quiz">{t.nav.cta}</Link>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          )}
        </div>
      </div>
    </nav>
  );
};
